import { useEffect, useRef } from "react";
import Box from "@mui/material/Box";
import { useTranslationApp } from "../../../lib/i18next";
import {
  TEMPLATES_TEXT_COLLISION_ONE,
  TEMPLATES_TEXT_COLLISION_OTHER,
  TEMPLATES_TEXT_COLLISION_UNNAMED_IMAGE,
  TEMPLATES_TEXT_COLLISION_UNNAMED_TEXT,
} from "../../../i18n/keysTranslations";
import { useBoardOverlayFrame } from "./useBoardOverlayFrame";
import { boardFrameClipPath } from "./boardFrameClipPath";

const AMBER = "#C2410C";
const AMBER_FILL = "rgba(249, 115, 22, 0.38)";

const layerLabel = ({ name, type, t }) => {
  const trimmed = String(name || "").trim();
  if (trimmed) return trimmed;
  return type === "image"
    ? t(TEMPLATES_TEXT_COLLISION_UNNAMED_IMAGE)
    : t(TEMPLATES_TEXT_COLLISION_UNNAMED_TEXT);
};

export const TextCollisionHighlight = ({
  boardRef,
  viewportRef,
  syncKey,
  polygon,
}) => {
  const overlayRef = useRef(null);
  const frame = useBoardOverlayFrame({
    boardRef,
    overlayRef,
    viewportRef,
    syncKey,
  });
  const points =
    frame.width > 0 && frame.height > 0 && polygon?.length
      ? polygon
          .map((point) => `${point.x * frame.width},${point.y * frame.height}`)
          .join(" ")
      : "";

  return (
    <Box
      ref={overlayRef}
      data-text-collision-highlight=""
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        clipPath: boardFrameClipPath(frame),
        pointerEvents: "none",
        zIndex: 4,
      }}
    >
      {points ? (
        <svg
          width={frame.width}
          height={frame.height}
          style={{
            position: "absolute",
            left: frame.left,
            top: frame.top,
            overflow: "visible",
          }}
        >
          <polygon
            points={points}
            fill={AMBER_FILL}
            stroke={AMBER}
            strokeWidth="1.5"
          />
        </svg>
      ) : null}
    </Box>
  );
};

const TextCollisionAlert = ({
  collisions = [],
  open = false,
  activeKey = null,
  onToggle,
  onClose,
  onSelect,
}) => {
  const { t } = useTranslationApp();
  const rootRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const count = collisions.length;
  const label =
    count === 1
      ? t(TEMPLATES_TEXT_COLLISION_ONE)
      : t(TEMPLATES_TEXT_COLLISION_OTHER, { count });

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current?.contains(event.target)) return;
      onCloseRef.current?.();
    };
    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  if (!count) return null;

  return (
    <Box
      sx={{
        position: "absolute",
        left: 12,
        right: 148,
        bottom: 12,
        zIndex: 7,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <Box
        ref={rootRef}
        onPointerDown={(event) => event.stopPropagation()}
        sx={{ position: "relative", pointerEvents: "auto", maxWidth: "100%" }}
      >
        {open ? (
          <Box
            role="listbox"
            aria-label={label}
            sx={{
              position: "absolute",
              left: "50%",
              bottom: "calc(100% + 8px)",
              transform: "translateX(-50%)",
              width: "max-content",
              minWidth: 240,
              maxWidth: "min(360px, 70vw)",
              maxHeight: 240,
              overflowY: "auto",
              p: 0.5,
              bgcolor: "#fff",
              border: "1px solid #FDBA74",
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(194, 65, 12, 0.12)",
            }}
          >
            {collisions.map((collision) => {
              const source = layerLabel({
                name: collision.sourceName,
                type: collision.sourceType,
                t,
              });
              const target = layerLabel({
                name: collision.targetName,
                type: collision.targetType,
                t,
              });
              const rowLabel = `${source} ↔ ${target}`;
              const selected = collision.key === activeKey;
              return (
                <Box
                  key={collision.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    borderRadius: "8px",
                    bgcolor: selected ? "#FFEDD5" : "transparent",
                    "&:hover": { bgcolor: selected ? "#FFEDD5" : "#FFF7ED" },
                  }}
                >
                  <Box
                    component="button"
                    type="button"
                    role="option"
                    aria-selected={selected}
                    onClick={() => onSelect?.(collision)}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      px: 1,
                      py: 0.75,
                      border: 0,
                      bgcolor: "transparent",
                      color: "#18181B",
                      textAlign: "left",
                      cursor: "pointer",
                      fontFamily: "inherit",
                      fontSize: "13px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {rowLabel}
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : null}
        <Box
          component="button"
          type="button"
          aria-expanded={open}
          onClick={onToggle}
          sx={{
            maxWidth: "100%",
            px: 1.5,
            py: 0.75,
            border: "1px solid #FDBA74",
            borderRadius: "999px",
            bgcolor: "#FFF7ED",
            color: AMBER,
            cursor: "pointer",
            fontFamily: "inherit",
            fontSize: "13px",
            fontWeight: 600,
            boxShadow: "0 4px 16px rgba(24,24,27,.08)",
            "&:hover": { bgcolor: "#FFEDD5" },
          }}
        >
          {label}
        </Box>
      </Box>
    </Box>
  );
};

export default TextCollisionAlert;
